import { IContainer } from '../../interface/container';
import { IFetcher, IFetchRequest, injectFetch, Method, ResponseTypes } from '../../interface/util';
import { IConfigAPI, injectConfigAPI } from '../../interface/config';
import { IMediumDiffRequestStatus, IMediaDiffResponse } from '../interface';
import { IMediaDiffAPI } from '../interface/mediaDiff';

interface IMediaDiffRequest {
    requestDto: {
        siteId: number;
        hash: string;
        requests: Array<{ id: string; mediumHash: string }>;
    };
}

function _request(params: {
    apiHref: string;
    authToken: string;
    siteId: number;
    siteHash?: string;
    existingMedia: Array<IMediumDiffRequestStatus>;
}): IFetchRequest {
    const mapMediumToBodyRequest = (
        medium: IMediumDiffRequestStatus,
    ): { id: string; mediumHash: string } => ({
        id: medium.id,
        mediumHash: medium.mediumHash,
    });

    const body: IMediaDiffRequest = {
        requestDto: {
            siteId: params.siteId,
            hash: params.siteHash ?? '',
            requests: (params.existingMedia ?? []).map(medium => mapMediumToBodyRequest(medium)),
        },
    };

    const req: IFetchRequest = {
        url: params.apiHref,
        method: 'POST',
        headers: [
            { name: 'Authorization', value: `Bearer ${params.authToken}` },
            { name: 'Content-Type', value: 'application/json' },
        ],
        body: JSON.stringify(body),
        expectedResponseType: 'json' as ResponseTypes,
    };

    return req;
}

function _isMediaDiffResponse(object: unknown): object is IMediaDiffResponse {
    if (!object || typeof object !== 'object') {
        return false;
    }
    const record: Partial<IMediaDiffResponse> = object as Partial<IMediaDiffResponse>;

    const isNullOrUndefined = (value: unknown) => value === null || value === undefined;

    return !isNullOrUndefined(record.value) && Array.isArray(record.value);
}

function getMediaDiff(
    fetcher: Promise<IFetcher>,
    configs: Array<Partial<IConfigAPI>>,
    authToken: string,
    siteId: number,
    siteHash = '',
    existingMedia: Array<IMediumDiffRequestStatus> = [],
): Promise<IMediaDiffResponse> {
    return new Promise(async function (resolve, reject) {
        try {
            const requester = await fetcher;

            const apiHrefs = configs
                .filter(config => config.url && config.extension?.mediaDiff)
                .map(config => `${config.url}${config.extension?.mediaDiff}`);

            for (const apiHref of apiHrefs) {
                const req = _request({
                    apiHref,
                    authToken,
                    siteId,
                    siteHash,
                    existingMedia,
                });

                const response = await requester(req).catch(error => {
                    console.error(
                        `Failed to fetch site data url:${apiHref}, error:${JSON.stringify(error)}`,
                    );
                    return null;
                });

                if (response && response.payload && _isMediaDiffResponse(response.payload)) {
                    resolve(response.payload);
                    return;
                }
            }

            reject(`Failed to resolve mediaDiff from hrefs: ${apiHrefs}`);
        } catch (error) {
            reject(error);
        }
    });
}

export default getMediaDiff;
